import { EMBED_INTERNAL_PARAM } from '@anm/constants/wave-editor';
import isMobile from '@anm/helpers/is/isMobile';
import noop from '@anm/helpers/noop';
import usePageParams from '@anm/hooks/usePageParams';
import { useCallback, FC } from 'react';
import { useSelector } from 'react-redux';

import { filterSelectors } from '../../../store/modules/filter';
import { templateSelectors } from '../../../store/modules/template';
import OpenEditor from '../../OpenEditor';
import AnonSignUpButton from '../../auth/AnonSignUpButton';

import { EditButtonWrapper } from './Wrapper';
import { getRatioFormatName, getTemplateId } from './helpers';
import { useInternalEmbedCallback } from './hooks';
import { EditButtonProps } from './types';

const EditButton: FC<EditButtonProps> = ({
  isSingleButton,
  onMobileEdit = noop,
  onTemplateEdit = noop,
  ...otherProps
}) => {
  const { folderId, embed } = usePageParams();
  const currentTemplate = useSelector(templateSelectors.selectCurrentTemplate);
  const { aspects } = useSelector(filterSelectors.selectTemplatesFilters);

  const ratioFormatName = getRatioFormatName({
    aspects,
    meta: currentTemplate?.meta,
    ...otherProps
  });

  const internalEmbedCallback = useInternalEmbedCallback(ratioFormatName, onTemplateEdit, currentTemplate?.templateId);

  const customizeParams = {
    ...(embed && { embed }),
    ...(folderId && { folderId }),
    format: ratioFormatName
  };
  const isInternalEmbed = embed === EMBED_INTERNAL_PARAM;

  const buttonText = isInternalEmbed ? 'Add' : isMobile() ? 'Edit on Desktop' : 'Edit Template';

  const isDisabled = isMobile();

  const handleMobileEdit = useCallback(() => {
    onMobileEdit();
  }, [onMobileEdit]);

  return (
    <EditButtonWrapper {...{ isSingleButton }}>
      <OpenEditor
        action="duplicate"
        params={customizeParams}
        isAuthControl={!embed}
        templateId={getTemplateId(currentTemplate?.templateId)}
      >
        {({ editorUrl, canOpenEditor, handleClick }) => (
          <AnonSignUpButton
            size="large"
            variant="light-blue-lowercase"
            href={canOpenEditor && !isInternalEmbed && !isDisabled ? editorUrl : undefined}
            onClick={isMobile() ? handleMobileEdit : isInternalEmbed ? internalEmbedCallback : handleClick}
          >
            {buttonText}
          </AnonSignUpButton>
        )}
      </OpenEditor>
    </EditButtonWrapper>
  );
};

export default EditButton;
